import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDownIcon } from 'lucide-react';
import { useAdmin } from '../../AdminContext';
import { adminNavGroups, collapsedNavGroups, sectionFor, type AdminNavGroup, type AdminNavItem } from '../../adminNav';
import type { NavCounts } from './useNavCounts';
type Props = {
  items: AdminNavItem[];
  /** 'sidebar' = compact (desktop); 'sheet' = grote tikvlakken (telefoonmenu). */
  variant: 'sidebar' | 'sheet';
  /** Wat er wacht per onderdeel (Inbox, Kassa); 0 of leeg = geen getal. */
  counts?: NavCounts;
};

/** Welke ingeklapte groepen Sarah zelf open heeft gezet; onthouden per browser. */
const STORAGE_KEY = 'be-nav-open';
const readOpen = (): AdminNavGroup[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const list: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((g): g is AdminNavGroup => adminNavGroups.includes(g as AdminNavGroup)) : [];
  } catch {
    return [];
  }
};

/**
 * Het volledige menu, per groep. Eén bron voor de zijbalk en het telefoonmenu, zodat ze nooit
 * uit elkaar lopen. Wat Sarah zelden aanpast (Aanbod, Website & marketing) staat ingeklapt, maar
 * gaat vanzelf open als ze er is of als er iets gewijzigd is. Instellingen staat onderaan, los van
 * het dagelijkse werk; Beveiliging zit bij je naam (AdminAccount).
 */
export function AdminNavList({
  items,
  variant,
  counts = {}
}: Props) {
  const {
    changed
  } = useAdmin();
  const {
    pathname
  } = useLocation();
  const [opened, setOpened] = useState<AdminNavGroup[]>(readOpen);
  const sheet = variant === 'sheet';
  const activeGroup = sectionFor(pathname, items)?.group;
  const beheer = items.filter(n => n.group === 'Beheer');
  const toggle = (g: AdminNavGroup) => setOpened(list => {
    const next = list.includes(g) ? list.filter(x => x !== g) : [...list, g];
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Privémodus of vol: dan alleen voor nu open.
    }
    return next;
  });
  const linkCls = (isActive: boolean) => `flex items-center gap-3 rounded-md px-3 transition-colors duration-150 ease-soft ${sheet ? 'min-h-[48px] text-[15px]' : 'py-1.5 text-[14px]'} ${isActive ? 'bg-white text-ink shadow-sm ring-1 ring-line' : 'text-muted hover:bg-white/60 hover:text-ink'}`;
  const isChanged = (n: AdminNavItem) => n.areas?.some(a => changed[a]) ?? false;
  const link = (n: AdminNavItem) => <li key={n.key} data-id="element-5218" data-mp="267hte0jg1dzr">
      <NavLink to={n.to} end={n.path === ''} className={({
      isActive
    }) => linkCls(isActive)} data-id="element-5219" data-mp="266pvgj2dj46b">
        <n.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden="true" data-id="element-5220" data-mp="1s6ovr6xui2sf" />
        <span className="min-w-0 flex-1 truncate" data-id="element-5221" data-mp="1x5iat3ss61up">{n.label}</span>
        {(counts[n.key] ?? 0) > 0 && <span className="shrink-0 rounded-full bg-clay px-1.5 py-px text-[11px] leading-4 tabular-nums text-shell" data-id="element-5222" data-mp="grictydhwma3">
            {counts[n.key]}
            <span className="sr-only" data-id="element-5223" data-mp="lp1t13tr794o"> wachten</span>
          </span>}
        {/* Nog niet gebouwd: rustig gezegd, zodat niemand denkt dat het kapot is. */}
        {n.comingSoon && <span className="shrink-0 text-[12px] text-muted" data-id="element-5224" data-mp="9ava82k22z27">binnenkort</span>}
        {isChanged(n) && <>
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-clay" aria-hidden="true" data-id="element-5225" data-mp="i9h06iruxgd0" />
            <span className="sr-only" data-id="element-5226" data-mp="i7nup6szd26l">(gewijzigd)</span>
          </>}
      </NavLink>
    </li>;
  const prefix = sheet ? 'admin-sheet' : 'admin-nav';
  return <nav aria-label="Beheer" className={sheet ? 'px-3 pb-4' : 'flex flex-1 flex-col overflow-y-auto px-3 py-5'} data-id="element-5227" data-mp="3lrs6z50ck8l0">
      <div className={sheet ? 'space-y-4' : 'space-y-5'} data-id="element-5228" data-mp="4bsfbaxsi97y">
        {adminNavGroups.filter(g => g !== 'Beheer').map(g => {
        const groupItems = items.filter(n => n.group === g);
        if (groupItems.length === 0) return null;
        const headingId = `${prefix}-${g.toLowerCase().replace(/[^a-z]+/g, '-')}`;
        const collapsible = collapsedNavGroups.has(g);
        const anyChanged = groupItems.some(isChanged);
        const open = !collapsible || opened.includes(g) || activeGroup === g || anyChanged;

        // Actief of gewijzigd: gewoon open met een kop, zodat de huidige plek nooit wegvalt.
        if (!collapsible || activeGroup === g || anyChanged) return <div key={g} data-id="element-5229" data-mp="2oxr2516g9k6r">
                  <p id={headingId} className="px-3 pb-1 text-[12px] text-muted" data-id="element-5230" data-mp="2oykcsd8j4tu6">
                    {g}
                  </p>
                  <ul aria-labelledby={headingId} className="space-y-0.5" data-id="element-5231" data-mp="2yx03wg7k433o">
                    {groupItems.map(link)}
                  </ul>
                </div>;
        return <div key={g} data-id="element-5232" data-mp="14iyux2ialzpv">
                <button type="button" id={headingId} aria-expanded={open} aria-controls={`${headingId}-list`} onClick={() => toggle(g)} className={`flex w-full items-center justify-between gap-2 rounded-md px-3 text-left text-[12px] text-muted transition-colors duration-150 ease-soft hover:text-ink ${sheet ? 'min-h-[40px]' : 'pb-1'}`} data-id="element-5233" data-mp="14jqqwefsxzgo">
                  <span data-id="element-5234" data-mp="r8vl4alyyewg">
                    {g}
                    {!open && <span className="ml-1.5 tabular-nums" data-id="element-5235" data-mp="naxv55kdjos0">· {groupItems.length}</span>}
                  </span>
                  <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-150 ease-soft ${open ? 'rotate-180' : ''}`} strokeWidth={1.75} aria-hidden="true" data-id="element-5236" data-mp="1jzdqxje2uudk" />
                </button>
                {open && <ul id={`${headingId}-list`} aria-labelledby={headingId} className="space-y-0.5" data-id="element-5237" data-mp="1b121vlsv04n8">
                    {groupItems.map(link)}
                  </ul>}
              </div>;
      })}
      </div>
      {beheer.length > 0 && <ul aria-label="Instellingen" className="mt-6 space-y-0.5 border-t border-line pt-4" data-id="element-5238" data-mp="3qosbeje9ijgc">
          {beheer.map(link)}
        </ul>}
    </nav>;
}